import { useId, useState } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Download, FileSpreadsheet, History, Search } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ErrorState } from "@/components/shared/ErrorState";
import { customerGroupsQueryKey } from "@/components/shared/customerGroupsQueryKey";
import { EmptyState } from "@/pages/CustomerPortal/Financial/component/EmptyState";
import { useDebounce } from "@/hooks/useDebounce";
import {
  CustomerGroup,
  CustomerGroupHistoryAction,
  CustomerGroupHistoryEntry,
  CustomerGroupImportItem,
  CustomerGroupImportResult,
  customerGroupsApi,
} from "@/lib/api";
import { CustomerGroupImportPlan, planCustomerGroupImport } from "@/lib/csvImportBatches";
import { formatDateTime } from "@/lib/formatters";
import { ClientGroupImportCard } from "./component/ClientGroupImportCard";
import { CsvBatchImportControls } from "./component/CsvBatchImportControls";
import { RejectedRowsTable } from "./component/RejectedRowsTable";
import { SearchableColumn, SearchableTable } from "./component/SearchableTable";
import { PAGE_SIZES } from "./component/pagination";
import { TablePagination } from "./component/TablePagination";
import { MAX_REJECTED_IN_SUMMARY, downloadRejected, sortByLine } from "./component/csvImport";
import { useCsvBatchImport } from "./component/useCsvBatchImport";

// details.type do resumo da importação de grupos por cliente (CustomerGroupHistoryImportTypes.ClientGroups).
const CLIENT_GROUPS_IMPORT_TYPE = "clientGroups";

const registeredGroupsQueryKey = [...customerGroupsQueryKey, "registered"] as const;
// Sob customerGroupsQueryKey: a confirmação da importação invalida a lista e o histórico juntos.
const historyQueryKey = [...customerGroupsQueryKey, "history"] as const;

const HISTORY_ACTION_LABELS: Record<string, { label: string; variant: "success" | "secondary" | "destructive" | "outline" }> = {
  Import: { label: "Importação", variant: "outline" },
  Create: { label: "Grupo criado", variant: "success" },
  Update: { label: "Grupo alterado", variant: "secondary" },
  Deactivate: { label: "Grupo desativado", variant: "destructive" },
  Reactivate: { label: "Grupo reativado", variant: "success" },
};

/** Texto da coluna "Detalhe": contagens da importação ou o que mudou no grupo. */
function describeHistoryEntry(entry: CustomerGroupHistoryEntry): string {
  if (entry.action === "Import") {
    const counts = entry.details?.counts ?? {};
    const countsText =
      entry.details?.type === CLIENT_GROUPS_IMPORT_TYPE
        ? `Grupos por cliente: ${counts.linked ?? 0} ligado(s), ${counts.moved ?? 0} trocaram de grupo, ${counts.unchanged ?? 0} inalterado(s), ${counts.rejected ?? 0} rejeitado(s)`
        : `${counts.inserted ?? 0} inserido(s), ${counts.updated ?? 0} atualizado(s), ${counts.unchanged ?? 0} inalterado(s), ${counts.rejected ?? 0} rejeitado(s)`;
    return [entry.details?.fileName ? `Arquivo ${entry.details.fileName}` : null, countsText].filter(Boolean).join(" · ");
  }
  const { previous, current } = entry;
  if (!previous && current) {
    return [
      current.magentoGroupId != null ? `ID Magento ${current.magentoGroupId}` : null,
      `código ${current.code}`,
      `nome ${current.name}`,
    ].filter(Boolean).join(", ");
  }
  if (!previous || !current) return "—";
  const changes = [
    previous.code !== current.code ? `código ${previous.code} → ${current.code}` : null,
    previous.name !== current.name ? `nome ${previous.name} → ${current.name}` : null,
    previous.isActive !== current.isActive ? (current.isActive ? "reativado" : "desativado") : null,
    previous.magentoGroupId !== current.magentoGroupId
      ? `ID Magento ${previous.magentoGroupId ?? "—"} → ${current.magentoGroupId ?? "—"}`
      : null,
  ].filter(Boolean);
  return changes.length > 0 ? changes.join(" · ") : "—";
}

function GroupHistoryTable({ entries }: { entries: CustomerGroupHistoryEntry[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead scope="col">Data</TableHead>
          <TableHead scope="col">Ação</TableHead>
          <TableHead scope="col">Grupo</TableHead>
          <TableHead scope="col">Detalhe</TableHead>
          <TableHead scope="col">Por</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {entries.map((entry) => {
          const action = HISTORY_ACTION_LABELS[entry.action] ?? { label: entry.action, variant: "outline" as const };
          return (
            <TableRow key={entry.id}>
              <TableCell className="whitespace-nowrap text-sm">{formatDateTime(new Date(entry.performedAt))}</TableCell>
              <TableCell><Badge variant={action.variant}>{action.label}</Badge></TableCell>
              <TableCell className="font-mono text-sm">{entry.groupCode ?? entry.current?.code ?? "—"}</TableCell>
              <TableCell className="text-sm">{describeHistoryEntry(entry)}</TableCell>
              <TableCell className="text-sm text-muted-foreground">{entry.performedBy ?? "—"}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

const describePrevious = (item: CustomerGroupImportItem) =>
  [
    item.previousCode !== item.code ? `código: ${item.previousCode}` : null,
    item.previousName !== item.name ? `nome: ${item.previousName}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

const importItemColumns: SearchableColumn<CustomerGroupImportItem>[] = [
  { id: "line", header: "Linha", cell: (item) => item.line, searchValue: (item) => item.line },
  {
    id: "magentoGroupId",
    header: "ID Magento",
    cell: (item) => item.magentoGroupId,
    searchValue: (item) => item.magentoGroupId,
    className: "font-mono",
  },
  { id: "code", header: "Código", cell: (item) => item.code, searchValue: (item) => item.code, className: "font-mono" },
  { id: "name", header: "Nome", cell: (item) => item.name, searchValue: (item) => item.name },
];

const importItemColumnsWithPrevious: SearchableColumn<CustomerGroupImportItem>[] = [
  ...importItemColumns,
  {
    id: "previous",
    header: "Antes",
    cell: describePrevious,
    searchValue: describePrevious,
    className: "text-sm text-muted-foreground",
  },
];

function ImportItemsTable({ items, showPrevious = false }: { items: CustomerGroupImportItem[]; showPrevious?: boolean }) {
  return (
    <SearchableTable
      rows={items}
      columns={showPrevious ? importItemColumnsWithPrevious : importItemColumns}
      rowKey={(item) => `${item.line}-${item.magentoGroupId}`}
      itemLabel="grupos"
      emptyTitle="Nenhuma linha"
      emptyMessage="Nenhum grupo nesta situação."
    />
  );
}

/** Resultado somado dos lotes, com o que depende do arquivo inteiro (calculado na tela, ao fim dos lotes). */
interface GroupImportView extends CustomerGroupImportResult {
  /** Linhas do arquivo; as que só repetem um grupo já visto não vão para a API. */
  fileLines: number;
  /** Grupos cadastrados (com ID do Magento) que não aparecem no arquivo; nunca são apagados. */
  missingFromFile: CustomerGroup[];
}

function emptyGroupResult(dryRun: boolean): GroupImportView {
  return {
    dryRun,
    counts: { totalLines: 0, inserted: 0, updated: 0, unchanged: 0, rejected: 0 },
    inserted: [],
    updated: [],
    unchanged: [],
    rejected: [],
    fileLines: 0,
    missingFromFile: [],
  };
}

function mergeGroupResults(total: GroupImportView, batch: CustomerGroupImportResult): GroupImportView {
  return {
    ...total,
    counts: {
      totalLines: total.counts.totalLines + batch.counts.totalLines,
      inserted: total.counts.inserted + batch.counts.inserted,
      updated: total.counts.updated + batch.counts.updated,
      unchanged: total.counts.unchanged + batch.counts.unchanged,
      rejected: total.counts.rejected + batch.counts.rejected,
    },
    inserted: total.inserted.concat(batch.inserted),
    updated: total.updated.concat(batch.updated),
    unchanged: total.unchanged.concat(batch.unchanged),
    rejected: total.rejected.concat(batch.rejected),
  };
}

function ImportResultView({ result, fileName }: { result: GroupImportView; fileName: string }) {
  const { counts } = result;
  const summary = [
    { label: result.dryRun ? "Serão inseridos" : "Inseridos", value: counts.inserted, className: "text-emerald-600" },
    { label: result.dryRun ? "Serão atualizados" : "Atualizados", value: counts.updated, className: "text-blue-600" },
    { label: "Inalterados", value: counts.unchanged, className: "" },
    { label: "Rejeitados", value: counts.rejected, className: counts.rejected > 0 ? "text-destructive" : "" },
  ];

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-4">
        {summary.map((item) => (
          <Card key={item.label}>
            <CardContent className="p-5">
              <p className="text-sm text-muted-foreground">{item.label}</p>
              <p className={`mt-1 text-2xl font-bold ${item.className}`}>{item.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          {result.fileLines} linha(s) no arquivo, {counts.totalLines} grupo(s) distinto(s).
          {result.missingFromFile.length > 0 &&
            ` ${result.missingFromFile.length} grupo(s) cadastrado(s) não vieram no arquivo e serão mantidos.`}
        </p>
        {result.rejected.length > 0 && (
          <Button variant="outline" size="sm" onClick={() => downloadRejected(result.rejected, fileName)}>
            <Download className="mr-2 h-4 w-4" aria-hidden="true" />
            Baixar rejeitadas (CSV)
          </Button>
        )}
      </div>

      <Tabs defaultValue={counts.rejected > 0 ? "rejected" : "inserted"}>
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="inserted">Inseridos ({counts.inserted})</TabsTrigger>
          <TabsTrigger value="updated">Atualizados ({counts.updated})</TabsTrigger>
          <TabsTrigger value="unchanged">Inalterados ({counts.unchanged})</TabsTrigger>
          <TabsTrigger value="rejected">Rejeitados ({counts.rejected})</TabsTrigger>
          {result.missingFromFile.length > 0 && (
            <TabsTrigger value="missing">Fora do arquivo ({result.missingFromFile.length})</TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="inserted"><ImportItemsTable items={result.inserted} /></TabsContent>
        <TabsContent value="updated"><ImportItemsTable items={result.updated} showPrevious /></TabsContent>
        <TabsContent value="unchanged"><ImportItemsTable items={result.unchanged} /></TabsContent>
        <TabsContent value="rejected"><RejectedRowsTable rows={result.rejected} /></TabsContent>
        <TabsContent value="missing">
          <GroupsTable groups={result.missingFromFile} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

const groupColumns: SearchableColumn<CustomerGroup>[] = [
  {
    id: "magentoGroupId",
    header: "ID Magento",
    cell: (group) => group.magentoGroupId ?? "—",
    searchValue: (group) => group.magentoGroupId,
    className: "font-mono",
  },
  { id: "code", header: "Código", cell: (group) => group.code, searchValue: (group) => group.code, className: "font-mono" },
  { id: "name", header: "Nome", cell: (group) => group.name, searchValue: (group) => group.name },
  {
    id: "status",
    header: "Situação",
    cell: (group) => (
      <Badge variant={group.isActive ? "success" : "secondary"}>{group.isActive ? "Ativo" : "Inativo"}</Badge>
    ),
    searchValue: (group) => (group.isActive ? "Ativo" : "Inativo"),
  },
  {
    id: "importedAt",
    header: "Importado em",
    cell: (group) => (group.importedAt ? formatDateTime(new Date(group.importedAt)) : "—"),
    className: "text-sm text-muted-foreground",
  },
];

function GroupsTable({ groups }: { groups: CustomerGroup[] }) {
  return (
    <SearchableTable
      rows={groups}
      columns={groupColumns}
      rowKey={(group) => String(group.id)}
      itemLabel="grupos"
      emptyTitle="Nenhum grupo"
      emptyMessage="Nenhum grupo nesta situação."
    />
  );
}

const ALL_ACTIONS = "all";

/** Histórico de alterações, com pesquisa, filtro de ação e paginação feitos na API (mais recente primeiro). */
function HistoryTable() {
  const searchId = useId();
  const actionId = useId();
  const [search, setSearch] = useState("");
  const [action, setAction] = useState<CustomerGroupHistoryAction | typeof ALL_ACTIONS>(ALL_ACTIONS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[1]);
  const debouncedSearch = useDebounce(search.trim(), 300);
  const actionFilter = action === ALL_ACTIONS ? undefined : action;
  const hasFilter = !!debouncedSearch || !!actionFilter;

  const query = useQuery({
    queryKey: [...historyQueryKey, { search: debouncedSearch, action: actionFilter, page, pageSize }],
    queryFn: () => customerGroupsApi.history({ search: debouncedSearch, action: actionFilter, page, pageSize }),
    placeholderData: keepPreviousData,
  });
  const entries = query.data?.data ?? [];
  const total = query.data?.total ?? 0;

  if (query.isLoading) {
    return (
      <div className="space-y-2" aria-busy="true">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
    );
  }
  if (query.isError) {
    return <ErrorState message="Não foi possível carregar o histórico." onRetry={() => void query.refetch()} />;
  }
  if (total === 0 && !hasFilter) {
    return <EmptyState title="Nenhuma alteração registrada" message="O histórico começa na primeira importação confirmada." />;
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <Label htmlFor={searchId} className="sr-only">Pesquisar no histórico</Label>
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input
            id={searchId}
            placeholder="Pesquisar por grupo (código, nome ou ID do Magento) ou por quem fez..."
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            className="pl-10"
          />
        </div>
        <div className="sm:w-56">
          <Label htmlFor={actionId} className="sr-only">Ação</Label>
          <Select
            value={action}
            onValueChange={(value) => {
              setAction(value as CustomerGroupHistoryAction | typeof ALL_ACTIONS);
              setPage(1);
            }}
          >
            <SelectTrigger id={actionId}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_ACTIONS}>Todas as ações</SelectItem>
              {Object.entries(HISTORY_ACTION_LABELS).map(([value, { label }]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {entries.length === 0 ? (
        <EmptyState title="Nenhum resultado" message="Nenhuma alteração corresponde à pesquisa." className="py-6" />
      ) : (
        <GroupHistoryTable entries={entries} />
      )}
      <TablePagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        disabled={query.isPlaceholderData}
      />
    </div>
  );
}

/** Grupos cadastrados (ativos e inativos), com pesquisa e paginação feitas na API. */
function RegisteredGroupsTable() {
  const searchId = useId();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const debouncedSearch = useDebounce(search.trim(), 300);

  const query = useQuery({
    queryKey: [...registeredGroupsQueryKey, { search: debouncedSearch, page, pageSize }],
    queryFn: () => customerGroupsApi.list({ search: debouncedSearch, status: "all", page, pageSize }),
    placeholderData: keepPreviousData,
  });
  const groups = query.data?.data ?? [];
  const total = query.data?.total ?? 0;

  if (query.isLoading) {
    return (
      <div className="space-y-2" aria-busy="true">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
    );
  }
  if (query.isError) {
    return <ErrorState message="Não foi possível carregar os grupos." onRetry={() => void query.refetch()} />;
  }
  if (total === 0 && !debouncedSearch) {
    return <EmptyState title="Nenhum grupo cadastrado" message="Importe o CSV do Magento para cadastrar os grupos." />;
  }

  return (
    <div className="space-y-3">
      <div className="relative">
        <Label htmlFor={searchId} className="sr-only">Pesquisar grupos cadastrados</Label>
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
        <Input
          id={searchId}
          placeholder="Pesquisar por código, nome ou ID do Magento..."
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          className="pl-10"
        />
      </div>
      {groups.length === 0 ? (
        <EmptyState title="Nenhum resultado" message="Nenhum grupo corresponde à pesquisa." className="py-6" />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              {groupColumns.map((column) => (
                <TableHead key={column.id} scope="col">{column.header}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {groups.map((group) => (
              <TableRow key={group.id}>
                {groupColumns.map((column) => (
                  <TableCell key={column.id} className={column.className}>{column.cell(group)}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <TablePagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        disabled={query.isPlaceholderData}
      />
    </div>
  );
}

/**
 * Painel Administrativo > Importar grupos de cliente: carga única dos grupos do Magento (prévia e confirmação), na
 * retirada do Magento, a partir do CSV de grupos ou do export de clientes (o mesmo arquivo do card de grupos por
 * cliente). O arquivo vai em lotes (useCsvBatchImport). O cadastro do dia a dia fica em Backoffice > Clientes >
 * Grupos de cliente. Só administradores (rota sob AdminRouteGuard).
 */
export default function CustomerGroupImport() {
  const queryClient = useQueryClient();


  const importState = useCsvBatchImport<GroupImportView, CustomerGroupImportPlan, CustomerGroupImportResult>({
    plan: (content) => planCustomerGroupImport(content),
    emptyResult: emptyGroupResult,
    merge: mergeGroupResults,
    sendBatch: (batch, dryRun, signal) => customerGroupsApi.importBatch(batch, dryRun, signal),
    // Fora do arquivo depende do arquivo inteiro, então é pedido à API depois dos lotes, com os IDs do arquivo.
    finish: async (result, plan) => {
      const missing = (await customerGroupsApi.missingFromFile(plan.magentoGroupIds)).data ?? [];
      return {
        ...result,
        inserted: sortByLine(result.inserted),
        updated: sortByLine(result.updated),
        unchanged: sortByLine(result.unchanged),
        rejected: sortByLine(result.rejected),
        fileLines: plan.totalLines,
        missingFromFile: missing,
      };
    },
    complete: (importId, file, result, signal) =>
      customerGroupsApi.completeImport(
        importId,
        {
          ...file,
          counts: { ...result.counts, totalLines: result.fileLines },
          rejected: result.rejected.slice(0, MAX_REJECTED_IN_SUMMARY),
          missingFromFile: result.missingFromFile.map((group) => ({ magentoGroupId: group.magentoGroupId!, code: group.code })),
        },
        signal,
      ),
    onSaved: async (result) => {
      toast.success("Importação concluída.", {
        description: `${result.counts.inserted} inserido(s), ${result.counts.updated} atualizado(s), ${result.counts.rejected} rejeitado(s).`,
      });
      await queryClient.invalidateQueries({ queryKey: customerGroupsQueryKey });
    },
  });


  return (
    <Layout title="Importar grupos de cliente">
      <div className="space-y-6">
        <p className="text-sm text-muted-foreground">
          Use o export de clientes do Magento nas duas importações, nesta ordem: primeiro os grupos, depois os grupos por
          cliente. Em cada importação, gere a prévia, confira o resultado e confirme. Grupos que já existem são
          atualizados pelo ID do Magento e nenhum grupo é apagado.
        </p>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileSpreadsheet className="h-5 w-5" aria-hidden="true" />
              Importar grupos de cliente
            </CardTitle>
            <CardDescription>
              Export de clientes do Magento (colunas <span className="font-mono">Customer Group ID</span> e{" "}
              <span className="font-mono">Customer Group Description</span>, que vira código e nome do grupo) ou CSV com
              cabeçalho <span className="font-mono">magento_group_id,code,name</span> (name opcional). UTF-8, separado por
              vírgula ou ponto e vírgula, até 10 MB. Cada grupo é enviado uma vez, em lotes: se parar no meio, retome ou
              envie o arquivo de novo, sem duplicar nada.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <CsvBatchImportControls
              state={importState}
              selectLabel="Selecionar arquivo CSV"
              describePlan={(plan) =>
                `${plan.totalLines} linha(s) no arquivo, ${plan.sentLines} grupo(s) distinto(s) em ${plan.batches.length} lote(s).`
              }
              hasChanges={(result) => result.counts.inserted + result.counts.updated > 0}
              nothingToSaveMessage="Nada a gravar: o arquivo não traz grupos novos nem alterados."
              renderResult={(result, fileName) => <ImportResultView result={result} fileName={fileName} />}
            />
          </CardContent>
        </Card>

        <ClientGroupImportCard />

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Grupos cadastrados</CardTitle>
            <CardDescription>Conferência dos grupos disponíveis para os clientes, ativos e inativos.</CardDescription>
          </CardHeader>
          <CardContent>
            <RegisteredGroupsTable />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <History className="h-5 w-5" aria-hidden="true" />
              Histórico de alterações
            </CardTitle>
            <CardDescription>
              Cada importação confirmada e cada grupo criado ou alterado, com quem fez. Mais recentes primeiro.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <HistoryTable />
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
